import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import LoginPage from './Pages/loginPage/LoginPage';
import Navbar from './Components/Navbar';
import ApiPollingService from './ApiPollingService';
import RedirectHandler from './RedirectHandler';
import ErrorBoundary from './Components/ErrorBoundary';
import ProtectedRoute from './Components/ProtectedRoute';
import { clearAllStorage, getRedirectURL } from './utils/cookieUtils';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SkipLink from './Components/SkipLink';
import LoadingScreen from './Components/LoadingScreen';

const UploadScreen = lazy(() => import('./Pages/salesCoachPage/UploadScreen'));
const UploadRecordingScreen = lazy(() => import('./Pages/salesCoachPage/UploadRecordingScreen'));
const Interactions = lazy(() => import('./Pages/salesCoachPage/Interactions'));
const ExecutiveReports = lazy(() => import('./Pages/salesCoachPage/ExecutiveReports'));
const PolicyBot = lazy(() => import('./Pages/PolicyBot/ChatInterface'));
const Admin = lazy(() => import('./Pages/admin/AdminPage'));
const QuestionPage = lazy(() => import('./Pages/callReportPage/QuestionPage'));
const PolicyBotHomePage = lazy(() => import('./Pages/PolicyBot/PolicyBotHomePage'));
const RecordScreen = lazy(() => import('./Pages/salesCoachPage/RecordScreen'));
const TranscriptScreen = lazy(() => import('./Pages/salesCoachPage/TranscriptScreen'));
const AnswerPage = lazy(() => import('./Pages/callReportPage/AnswerPage'));

const VERSION_CHECK_INTERVAL_MS = 60 * 1000;

const checkForUpdate = async () => {
  try {
    const response = await fetch("/meta.json?v=" + new Date().getTime()); // Prevent cache
    if (!response.ok) return;
    const { version } = await response.json();
    const storedVersion = localStorage.getItem("app_version");
    if (storedVersion && storedVersion !== version) {
      localStorage.setItem("app_version", version);
      clearCacheAndReload();
    } else {
      localStorage.setItem("app_version", version);
    }
  } catch (error) {
    console.error("Version check failed", error);
  }
};
const clearCacheAndReload = async () => {
  alert("A new version of the app is available. Please re-login.");

  const cacheKeys = await caches.keys();
  await Promise.all(cacheKeys.map((name) => caches.delete(name)));

  clearAllStorage();
  window.location.href = getRedirectURL();
};




function AppContent() {
  const { isAuthenticated, loading, isLoggedIn, fetchProtectedData, handleLogout } = useAuth();

  useEffect(() => {
    const interval = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        <SkipLink targetId="main-content" />
        <RedirectHandler />
        <Navbar />
        <main id="main-content" role="main" tabIndex="-1" style={{ outline: 'none' }}>
          {loading ? (
            <LoadingScreen message="Loading AgriIntel workspace" />
          ) : (
            <Suspense fallback={<LoadingScreen message="Loading page components" />}>
              <Routes>
              {/* Login route */}
              <Route
                path="/"
                element={isAuthenticated ? <Navigate to="/sage" /> : <LoginPage />}
              />
              <Route
                path="/salescoach"
                element={isAuthenticated ? <UploadScreen /> : <Navigate to="/" />}
              />
              <Route
                path="/record"
                element={isAuthenticated ? <RecordScreen /> : <Navigate to="/" />}
              />
              <Route
                path="/transcript"
                element={isAuthenticated ? <TranscriptScreen /> : <Navigate to="/" />}
              />
              <Route
                path="/uploadrecording"
                element={isAuthenticated ? <UploadRecordingScreen /> : <Navigate to="/" />}
              />
              <Route
                path="/interactions"
                element={isAuthenticated ? <Interactions /> : <Navigate to="/" />}
              />
              <Route
                path="/executive-report"
                element={isAuthenticated ? <ExecutiveReports /> : <Navigate to="/" />}
              />
              <Route
                path="/sage-chat"
                element={isAuthenticated ? <PolicyBot handleLogout={handleLogout} /> : <Navigate to="/" />}
              />
              <Route
                path="/sage"
                element={isAuthenticated ? <PolicyBotHomePage /> : <Navigate to="/" />}
              />

              {/* Admin route: ProtectedRoute verifies the role with the server */}
              <Route
                path="/admin"
                element={
                  isAuthenticated ? (
                    <ProtectedRoute requiredRole="admin" redirectTo="/sage">
                      <Admin />
                    </ProtectedRoute>
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/ask-questions"
                element={isAuthenticated ? <QuestionPage /> : <Navigate to="/" />}
              />
              <Route
                path="/answers-chat"
                element={isAuthenticated ? <AnswerPage /> : <Navigate to="/" />}
              />
              {/* Default route */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
          )}
          <ApiPollingService
            isLoggedIn={isLoggedIn}
            onPoll={fetchProtectedData}
            onLogout={handleLogout}
          />
        </main>
      </div>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;

